import {AnyAttrs, AttrsSchema, Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getCardInfo, submitSelfServeChange, submitSelfServePurchase} from 'media/js/pricing/api/api';
import {updateBillingEmail} from 'common/report/queries/app-api/legacy/billing';
import {addToast} from 'common/components/toast-area/toast-manager';
import {ToastType} from 'common/components/toast/types';
import type {PlanBuilder} from 'media/js/pricing/plan-builder-2023/index';
import {PlanBuilderState} from 'media/js/pricing/plan-builder-2023/types';
import {TrackingEvents, TrackingPropertyNames} from 'media/js/pricing/tracking';

import {template} from './template';
import './index.styl';

type ReviewState = {
  emails: string;
  showEmailInput: boolean;
  emailValidationError: string;
  purchaseBtnLoading: boolean;
};
export class ReviewPlan extends Component<ReviewState, AnyAttrs, PlanBuilderState, PlanBuilder> {
  static get attrsSchema(): AttrsSchema<AnyAttrs> {
    return {
      ...super.attrsSchema,
      'mp-2023-style': {
        type: `boolean`,
        description: `flag to check whether page should apply new 2023 stying`,
      },
    };
  }

  get config() {
    return {
      template,
      defaultState: {
        emails: ``,
        showEmailInput: false,
        emailValidationError: null,
        purchaseBtnLoading: false,
      },
      helpers: {
        purchasePlan: async () => {
          try {
            this.update({purchaseBtnLoading: true});
            const purchaseRequest = this.app.helpers.getPurchaseRequest();
            const purchaseResult = await submitSelfServePurchase(purchaseRequest);

            this.update({purchaseBtnLoading: false});
            if (purchaseResult) {
              this.app.helpers.trackEventWithPlanProps(TrackingEvents.PurchasePlan);
              this.helpers.handleContinue();
            }
          } catch (err) {
            this.update({purchaseBtnLoading: false});
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.ErrorDisplayed, {
              [TrackingPropertyNames.ErrorMessage]: err.message,
            });
            addToast({
              type: ToastType.ERROR,
              isError: true,
              message: err.message,
            });
          }
        },
        changePlan: async () => {
          try {
            this.update({purchaseBtnLoading: true});
            const changeRequest = this.app.helpers.getChangeRequest();
            const changeResult = await submitSelfServeChange(changeRequest);

            this.update({purchaseBtnLoading: false});
            if (changeResult) {
              this.app.helpers.trackEventWithPlanProps(TrackingEvents.ChangePlan);
              this.helpers.handleContinue();
            }
          } catch (err) {
            this.update({purchaseBtnLoading: false});
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.ErrorDisplayed, {
              [TrackingPropertyNames.ErrorMessage]: err.message,
            });
            addToast({
              type: ToastType.ERROR,
              isError: true,
              message: err.message,
            });
          }
        },
        setEmailRecipients: (emails) => {
          this.update({emails});
        },
        setShowEmailInput: (val) => this.update({showEmailInput: val}),
        setEmailValidationError: (val) => this.update({emailValidationError: val}),
        handleContinue: () => {
          this.app.helpers.goToNextStep();
        },
        handleInputGroupSubmit: async () => {
          try {
            const {accountId} = this.app.state.customerInfo.orgInfo;
            const result = await updateBillingEmail({bccEmail: this.state.emails, accountId});

            if (result) {
              this.helpers.setEmailValidationError(null);
              this.helpers.setShowEmailInput(false);
            }
          } catch (err) {
            this.app.helpers.trackEventWithPlanProps(TrackingEvents.ErrorDisplayed, {
              [TrackingPropertyNames.ErrorMessage]: err.message,
            });
            this.update({emailValidationError: `Please ensure email(s) are valid`});
          }
        },
      },
    };
  }

  public async connectedCallback() {
    super.connectedCallback();

    const {accountId} = this.app.state.customerInfo.orgInfo;
    const cardInfo = await getCardInfo(accountId);
    this.app.update({billingCard: cardInfo});

    this.app.helpers.handleUpdateBreakdown();
  }
}

export const ReviewEl = defineAndMakeJsxFactory(`review-plan-2023`, ReviewPlan);
